/**
 * NotificationPrompt — Banner that asks the user to enable push notifications.
 * Shows once per session when permission is 'default' (not yet decided).
 *
 * NotificationToast — Floating toast for foreground push messages.
 *
 * MandatoryPermissionsGate — Full-screen overlay that blocks the tech UI
 * until both push notifications and location permissions are granted.
 */

import { useState, useEffect } from 'react';
import { Bell, X, AlertTriangle, CheckCircle2, MapPin, Shield, Loader2 } from 'lucide-react';
import { usePushNotifications, type ForegroundNotification } from '@/hooks/usePushNotifications';

// ─── Permission Prompt Banner ───
// Slim inline banner shown below TechHeader when notifications aren't yet enabled.
// Disappears automatically once permission is granted (hook polls state).

interface PromptProps {
  userId: string | null;
}

export function NotificationPromptBanner({ userId }: PromptProps) {
  const { shouldPrompt, requestPermission, loading, permission } = usePushNotifications(userId);
  const [dismissed, setDismissed] = useState(false);
  const [justEnabled, setJustEnabled] = useState(false);

  if (dismissed || !shouldPrompt || permission === 'granted') {
    // Show brief success message
    if (justEnabled) {
      return (
        <div className="mx-4 mt-3 mb-1 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 animate-in fade-in duration-300">
          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
          <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
            Notifications enabled — you'll be alerted for SOS, job changes, and more.
          </span>
        </div>
      );
    }
    return null;
  }

  const handleEnable = async () => {
    const token = await requestPermission();
    if (token) {
      setJustEnabled(true);
      setTimeout(() => setJustEnabled(false), 4000);
    }
  };

  return (
    <div className="mx-4 mt-3 mb-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
      <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
        <Bell className="w-4 h-4 text-amber-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-gray-900 dark:text-white">Enable notifications</p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
          Get alerted for SOS, job changes, tech requests, and more.
        </p>
      </div>
      <button
        onClick={handleEnable}
        disabled={loading}
        className="px-3 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 shrink-0"
      >
        {loading ? 'Enabling...' : 'Enable'}
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="p-1 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Mandatory Permissions Gate ───
// Full-screen overlay blocking the tech UI until notifications + location are granted.
// Shown on top of TechLayout content when either permission is missing.

interface GateProps {
  userId: string | null;
  onAllGranted: () => void;
}

type PermissionStep = 'notifications' | 'location';

export function MandatoryPermissionsGate({ userId, onAllGranted }: GateProps) {
  const { supported: notifSupported, permission: notifPermission, requestPermission, loading: notifLoading } = usePushNotifications(userId);
  const [locationPermission, setLocationPermission] = useState<PermissionState | 'unsupported' | 'checking'>('checking');
  const [locationRequesting, setLocationRequesting] = useState(false);
  const [activeStep, setActiveStep] = useState<PermissionStep>('notifications');

  // Check location permission state
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationPermission('unsupported');
      return;
    }

    // Use Permissions API if available for real-time tracking
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then(result => {
        setLocationPermission(result.state);
        result.addEventListener('change', () => {
          setLocationPermission(result.state);
        });
      }).catch(() => {
        // Permissions API not available for geolocation — treat as unknown
        setLocationPermission('prompt');
      });
    } else {
      // No Permissions API — assume prompt needed
      setLocationPermission('prompt');
    }
  }, []);

  // Track whether both are granted
  const notifGranted = !notifSupported || notifPermission === 'granted';
  const locationGranted = locationPermission === 'granted' || locationPermission === 'unsupported';
  const stillChecking = locationPermission === 'checking';

  // Auto-advance from notifications to location step
  useEffect(() => {
    if (notifGranted && activeStep === 'notifications') {
      setActiveStep('location');
    }
  }, [notifGranted, activeStep]);

  // Notify parent when both granted (onAllGranted must be stable — wrapped in useCallback by caller)
  useEffect(() => {
    if (notifGranted && locationGranted) {
      onAllGranted();
    }
  }, [notifGranted, locationGranted]);  

  // If everything is already granted or still checking, don't render the gate
  if (notifGranted && locationGranted) return null;
  if (stillChecking && notifGranted) return null; // Avoid flash while checking location

  const handleNotificationEnable = async () => {
    await requestPermission();
  };

  const handleLocationEnable = () => {
    setLocationRequesting(true);
    navigator.geolocation.getCurrentPosition(
      () => {
        setLocationPermission('granted');
        setLocationRequesting(false);
      },
      () => {
        // Check if it was denied or just errored
        if (navigator.permissions) {
          navigator.permissions.query({ name: 'geolocation' }).then(result => {
            setLocationPermission(result.state);
          }).catch(() => setLocationPermission('denied'));
        } else {
          setLocationPermission('denied');
        }
        setLocationRequesting(false);
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const notifDenied = notifSupported && notifPermission === 'denied';
  const locationDenied = locationPermission === 'denied';

  return (
    <div className="fixed inset-0 z-[200] bg-gray-50 flex flex-col items-center justify-center p-6">
      {/* Header */}
      <div className="w-full max-w-sm text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-brand-primary/10 flex items-center justify-center mx-auto mb-5">
          <Shield className="w-8 h-8 text-brand-primary" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">Permissions Required</h1>
        <p className="text-sm text-gray-500 leading-relaxed">
          To keep the team safe and connected, we need notifications and location access before you start your day.
        </p>
      </div>

      {/* Permission Cards */}
      <div className="w-full max-w-sm space-y-3">
        {/* Notifications Card */}
        <div className={`rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
          notifGranted
            ? 'border-emerald-200 bg-emerald-50'
            : notifDenied
              ? 'border-red-200 bg-red-50'
              : activeStep === 'notifications'
                ? 'border-brand-secondary bg-white shadow-lg shadow-brand-secondary/10'
                : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="px-5 py-4 flex items-center gap-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
              notifGranted
                ? 'bg-emerald-100'
                : notifDenied
                  ? 'bg-red-100'
                  : 'bg-brand-secondary/10'
            }`}>
              {notifGranted ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              ) : (
                <Bell className={`w-5 h-5 ${notifDenied ? 'text-red-500' : 'text-brand-secondary'}`} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold ${notifGranted ? 'text-emerald-700' : notifDenied ? 'text-red-700' : 'text-gray-900'}`}>
                Push Notifications
              </p>
              <p className={`text-xs mt-0.5 ${notifGranted ? 'text-emerald-600' : notifDenied ? 'text-red-500' : 'text-gray-500'}`}>
                {notifGranted
                  ? 'Enabled — you\'ll receive SOS and job alerts'
                  : notifDenied
                    ? 'Blocked — open browser settings to allow'
                    : 'SOS alerts, job updates, requests'}
              </p>
            </div>
            {!notifGranted && !notifDenied && activeStep === 'notifications' && (
              <button
                onClick={handleNotificationEnable}
                disabled={notifLoading}
                className="px-4 py-2 bg-brand-secondary text-brand-primary text-xs font-bold rounded-xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 shrink-0"
              >
                {notifLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Allow'
                )}
              </button>
            )}
          </div>
        </div>

        {/* Location Card */}
        <div className={`rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
          locationGranted
            ? 'border-emerald-200 bg-emerald-50'
            : locationDenied
              ? 'border-red-200 bg-red-50'
              : activeStep === 'location'
                ? 'border-brand-secondary bg-white shadow-lg shadow-brand-secondary/10'
                : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="px-5 py-4 flex items-center gap-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
              locationGranted
                ? 'bg-emerald-100'
                : locationDenied
                  ? 'bg-red-100'
                  : activeStep === 'location'
                    ? 'bg-brand-secondary/10'
                    : 'bg-gray-100'
            }`}>
              {locationGranted ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              ) : (
                <MapPin className={`w-5 h-5 ${locationDenied ? 'text-red-500' : activeStep === 'location' ? 'text-brand-secondary' : 'text-gray-400'}`} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold ${locationGranted ? 'text-emerald-700' : locationDenied ? 'text-red-700' : activeStep === 'location' ? 'text-gray-900' : 'text-gray-400'}`}>
                Location Access
              </p>
              <p className={`text-xs mt-0.5 ${locationGranted ? 'text-emerald-600' : locationDenied ? 'text-red-500' : activeStep === 'location' ? 'text-gray-500' : 'text-gray-400'}`}>
                {locationGranted
                  ? 'Enabled — your position shows on the team map'
                  : locationDenied
                    ? 'Blocked — open browser settings to allow'
                    : 'Live map for dispatch and safety'}
              </p>
            </div>
            {!locationGranted && !locationDenied && activeStep === 'location' && (
              <button
                onClick={handleLocationEnable}
                disabled={locationRequesting}
                className="px-4 py-2 bg-brand-secondary text-brand-primary text-xs font-bold rounded-xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 shrink-0"
              >
                {locationRequesting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Allow'
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Denied State Help */}
      {(notifDenied || locationDenied) && (
        <div className="w-full max-w-sm mt-6 px-5 py-4 rounded-2xl bg-red-50 border border-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-red-700 mb-1">Permission Blocked</p>
              <p className="text-xs text-red-600 leading-relaxed">
                {notifDenied && locationDenied
                  ? 'Notifications and location were blocked. '
                  : notifDenied
                    ? 'Notifications were blocked. '
                    : 'Location was blocked. '}
                Open your browser settings, find this site, and change permissions to "Allow", then refresh.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700 active:scale-95 transition-all"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Skip button — lets the user proceed without granting permissions.
          Sets a localStorage flag via onAllGranted; TechLayout's mount-time
          re-verify clears the flag if browser permissions aren't actually
          granted, so the gate re-shows on the next session. */}
      <button
        onClick={onAllGranted}
        className="mt-6 text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
      >
        Skip for now
      </button>

      {/* Footer note */}
      <p className="mt-4 text-[11px] text-gray-400 text-center max-w-xs leading-relaxed">
        These permissions are required for all field staff. Location is only tracked during work hours and can be paused by the office.
      </p>
    </div>
  );
}

// ─── Foreground Notification Toast ───

interface ToastProps {
  notification: ForegroundNotification | null;
  onDismiss: () => void;
  onTap?: (url: string) => void;
}

const TYPE_STYLES: Record<string, { border: string; icon: string; bg: string }> = {
  sos: { border: 'border-red-500', icon: 'text-red-500', bg: 'bg-red-500/5' },
  flag: { border: 'border-amber-500', icon: 'text-amber-500', bg: 'bg-amber-500/5' },
  time_request: { border: 'border-orange-500', icon: 'text-orange-500', bg: 'bg-orange-500/5' },
  job_change: { border: 'border-blue-500', icon: 'text-blue-500', bg: 'bg-blue-500/5' },
  tech_consult: { border: 'border-purple-500', icon: 'text-purple-500', bg: 'bg-purple-500/5' },
  voting: { border: 'border-yellow-500', icon: 'text-yellow-500', bg: 'bg-yellow-500/5' },
  general: { border: 'border-gray-400', icon: 'text-gray-500', bg: 'bg-gray-500/5' },
};

export function NotificationToast({ notification, onDismiss, onTap }: ToastProps) {
  if (!notification) return null;

  const style = TYPE_STYLES[notification.type] || TYPE_STYLES.general;

  return (
    <div className="fixed top-4 right-4 z-[300] animate-in slide-in-from-top duration-300 max-w-sm w-full">
      <div
        className={`${style.bg} border-l-4 ${style.border} rounded-xl shadow-2xl p-4 cursor-pointer backdrop-blur-sm`}
        onClick={() => {
          onTap?.(notification.url);
          onDismiss();
        }}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className={`w-5 h-5 ${style.icon} shrink-0 mt-0.5`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900 dark:text-white">{notification.title}</p>
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 line-clamp-2">
              {notification.body}
            </p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            className="p-1 text-gray-400 hover:text-gray-600 shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
